import * as THREE from 'three';
import React, { useMemo } from 'react';

function ReturnChannel({ convRatio, color, drawingBaseDimensions }) {
  const nbrWindings = drawingBaseDimensions.nbr_windings;
  const reverseConfig = ['A', 'C', 'E', 'G', 'L'].includes(
    drawingBaseDimensions.configuration
  )
    ? 1
    : -1;
  const configAng = THREE.MathUtils.degToRad(
    reverseConfig === 1
      ? drawingBaseDimensions.configuration_angle
      : 360 - drawingBaseDimensions.configuration_angle
  );
  const width = convRatio * drawingBaseDimensions.belt_width;
  const thickness = convRatio * drawingBaseDimensions.return_channel_thickness;

  const inclination =
    THREE.MathUtils.degToRad(drawingBaseDimensions.slope_angle_center) *
    reverseConfig;
  const usedInclination =
    drawingBaseDimensions.u2_value > thickness
      ? 0
      : drawingBaseDimensions.ue_double_bend_value > thickness
        ? inclination / 2
        : inclination;

  const inclinationLe =
    THREE.MathUtils.degToRad(drawingBaseDimensions.slope_angle_le) *
    reverseConfig;
  const inclinationLe_db = inclinationLe / 2;
  const heightDiffLE =
    (Math.tan(inclinationLe) *
      reverseConfig *
      drawingBaseDimensions.le_angular +
      Math.tan(inclinationLe_db) *
        drawingBaseDimensions.le_double_bend_value *
        reverseConfig) *
    convRatio;

  const posYTop =
    drawingBaseDimensions.h3 * convRatio -
    Math.tan(usedInclination) * thickness * reverseConfig;
  const posYBot =
    (drawingBaseDimensions.h1 +
      drawingBaseDimensions.pitch * (nbrWindings - parseInt(nbrWindings, 10))) *
      convRatio -
    drawingBaseDimensions.winding_thickness * convRatio +
    // thickness +
    heightDiffLE;
  const height_rc = posYTop - posYBot;

  const radiusLength = convRatio * (drawingBaseDimensions.central_diam / 2);
  const UELength =
    drawingBaseDimensions.u1 * convRatio * reverseConfig -
    (thickness / 2) * reverseConfig +
    Math.sin(usedInclination) *
      drawingBaseDimensions.winding_thickness *
      convRatio;

  const posX =
    Math.sin(Math.PI * 2 - configAng) * UELength +
    Math.cos(configAng) * radiusLength;

  const posY = (posYTop + posYBot) / 2;
  const posZ =
    Math.cos(Math.PI * 2 - configAng) * UELength +
    Math.sin(configAng) * radiusLength;

  const posXreturn =
    (Math.sin(Math.PI * 2 - configAng) *
      drawingBaseDimensions.u1 *
      convRatio *
      reverseConfig) /
      2 +
    Math.cos(configAng) * radiusLength;
  const posZreturn =
    (Math.cos(Math.PI * 2 - configAng) *
      drawingBaseDimensions.u1 *
      convRatio *
      reverseConfig) /
      2 +
    Math.sin(configAng) * radiusLength;

  const materialProps = {
    color: color,
    roughness: 0.8,
    metalness: 0.5,
    side: THREE.DoubleSide,
  };

  const materials = [
    new THREE.MeshStandardMaterial(materialProps), // right
    new THREE.MeshStandardMaterial(materialProps), // left
    new THREE.MeshStandardMaterial({ ...materialProps, color: 'black' }), // top
    new THREE.MeshStandardMaterial({ ...materialProps, color: 'black' }), // bottom
    new THREE.MeshStandardMaterial({ ...materialProps, color: 'black' }), // front
    new THREE.MeshStandardMaterial(materialProps), // back
  ];

  const materialsReturn = [
    new THREE.MeshStandardMaterial(materialProps), // right
    new THREE.MeshStandardMaterial(materialProps), // left
    new THREE.MeshStandardMaterial(materialProps), // top
    new THREE.MeshStandardMaterial({ ...materialProps, color: 'black' }), // bottom
    new THREE.MeshStandardMaterial({ ...materialProps, color: 'black' }), // front
    new THREE.MeshStandardMaterial({ ...materialProps, color: 'black' }), // back
  ];

  return ['J-return top', 'J-return extra clearance'].includes(
    drawingBaseDimensions.special_return
  ) ? (
    <JReturn
      convRatio={convRatio}
      drawingBaseDimensions={drawingBaseDimensions}
      materialProps={materialProps}
      materialsReturn={materialsReturn}
    />
  ) : (
    <>
      <mesh
        position={[posX, posY, posZ]}
        rotation={[
          0,
          Math.PI * 2 - configAng + (reverseConfig === 1 ? 0 : Math.PI),
          0,
        ]}
      >
        <boxGeometry args={[width, height_rc, thickness]} />
        {materials.map((material, index) => (
          <primitive
            key={index}
            attach={`material-${index}`}
            object={material}
          />
        ))}
      </mesh>
      <mesh
        position={[posXreturn, posYBot, posZreturn]}
        rotation={[
          0,
          Math.PI * 2 - configAng + (reverseConfig === 1 ? 0 : Math.PI),
          0,
        ]}
      >
        <boxGeometry
          args={[
            width,
            thickness,
            drawingBaseDimensions.u1 * convRatio * reverseConfig,
          ]}
        />
        {materialsReturn.map((material, index) => (
          <primitive
            key={index}
            attach={`material-${index}`}
            object={material}
          />
        ))}
      </mesh>
    </>
  );
}

function JReturn({
  convRatio,
  drawingBaseDimensions,
  materialProps,
  materialsReturn,
}) {
  const slope = THREE.MathUtils.degToRad(
    drawingBaseDimensions.slope_angle_center
  );
  const inclinationLe_db = slope / 2;
  const distCentreRCJConfigFoot =
    drawingBaseDimensions.distance_centre_rc_j_config +
    (Math.sin(THREE.MathUtils.degToRad(4.1)) *
      (drawingBaseDimensions.central_diam + drawingBaseDimensions.belt_width)) /
      2;
  const distCentreRCJConfigFootExt =
    distCentreRCJConfigFoot + drawingBaseDimensions.return_channel_thickness;

  const config_45_135 =
    drawingBaseDimensions.configuration_angle >= 45 &&
    drawingBaseDimensions.configuration_angle <= 135;

  const heightDiffUe =
    Math.tan(slope) *
      (drawingBaseDimensions.ue_angular +
        drawingBaseDimensions.distance_centre_rc_j_config) +
    Math.tan(inclinationLe_db) * drawingBaseDimensions.ue_double_bend_value +
    (config_45_135 ? drawingBaseDimensions.pitch / 4 : 0);

  const inclinationLe = THREE.MathUtils.degToRad(
    drawingBaseDimensions.slope_angle_le
  );
  const inclinationLeDB = inclinationLe / 2;

  const heightDiffLE =
    Math.tan(inclinationLe) * drawingBaseDimensions.le_angular +
    Math.tan(inclinationLeDB) * drawingBaseDimensions.le_double_bend_value;

  const posYbase =
    convRatio *
    (drawingBaseDimensions.h3 -
      heightDiffUe -
      drawingBaseDimensions.winding_thickness);
  const posY =
    drawingBaseDimensions.special_return === 'J-return top'
      ? posYbase
      : posYbase - convRatio * drawingBaseDimensions.pitch;

  const posYLow =
    (((drawingBaseDimensions.configuration_angle + (config_45_135 ? 90 : 180)) /
      360) *
      drawingBaseDimensions.pitch +
      drawingBaseDimensions.h1 +
      heightDiffLE -
      drawingBaseDimensions.winding_thickness) *
    convRatio;

  const hVert =
    posY - posYLow - Math.tan(slope) * distCentreRCJConfigFootExt * convRatio;

  const reverseConfig = ['A', 'C', 'E', 'G', 'L'].includes(
    drawingBaseDimensions.configuration
  );

  const rotation = THREE.MathUtils.degToRad(
    (reverseConfig ? 180 : 0) +
      (reverseConfig ? -1 : 1) * drawingBaseDimensions.configuration_angle +
      (reverseConfig ? 1 : -1) * (config_45_135 ? 90 : 0)
  );
  const depth = convRatio * drawingBaseDimensions.return_channel_thickness;

  const geometry = useMemo(() => {
    const rExt =
      ((drawingBaseDimensions.central_diam + drawingBaseDimensions.belt_width) /
        2) *
      convRatio;
    const rInt =
      ((drawingBaseDimensions.central_diam - drawingBaseDimensions.belt_width) /
        2) *
      convRatio;

    const shape = new THREE.Shape();
    shape.moveTo(rExt, 0); // Départ sur le bord extérieur droit
    shape.absarc(0, 0, rExt, 0, Math.PI, false); // Demi-cercle extérieur
    shape.lineTo(-rInt, 0); // Revenir au bord intérieur gauche
    shape.absarc(0, 0, rInt, Math.PI, 0, true); // Demi-cercle intérieur (sens inverse)
    shape.closePath();

    return new THREE.ExtrudeGeometry(shape, {
      depth: depth,
      bevelEnabled: false,
    });
  }, [drawingBaseDimensions, convRatio]);

  const materials = useMemo(
    () => [
      new THREE.MeshStandardMaterial({ ...materialProps, color: 'black' }),
      new THREE.MeshStandardMaterial(materialProps),
    ],
    [materialProps]
  );

  return (
    <mesh rotation={[0, rotation, 0]}>
      <mesh
        position={[0, posY, convRatio * distCentreRCJConfigFoot]}
        rotation={[-THREE.MathUtils.degToRad(4.1), 0, 0]}
        geometry={geometry}
        material={materials}
      />
      <mesh
        position={[
          (drawingBaseDimensions.central_diam / 2) *
            convRatio *
            (reverseConfig ? -1 : 1),
          posY +
            ((Math.tan(slope) * distCentreRCJConfigFootExt) / 2 -
              drawingBaseDimensions.return_channel_thickness / 2) *
              convRatio,
          (distCentreRCJConfigFootExt / 2) * convRatio,
        ]}
        rotation={[slope, 0, 0]}
      >
        <boxGeometry
          args={[
            drawingBaseDimensions.belt_width * convRatio,
            depth,
            distCentreRCJConfigFootExt * convRatio,
          ]}
        />
        {materialsReturn.map((material, index) => (
          <primitive
            key={index}
            attach={`material-${index}`}
            object={material}
          />
        ))}
      </mesh>
      <mesh
        position={[
          (drawingBaseDimensions.central_diam / 2) *
            convRatio *
            (reverseConfig ? 1 : -1),
          posY - hVert / 2,
          ((distCentreRCJConfigFoot + distCentreRCJConfigFootExt) / 2) *
            convRatio,
        ]}
      >
        <boxGeometry
          args={[drawingBaseDimensions.belt_width * convRatio, hVert, depth]}
        />
        {materialsReturn.map((material, index) => (
          <primitive
            key={index}
            attach={`material-${index}`}
            object={material}
          />
        ))}
      </mesh>
      <mesh
        position={[
          (drawingBaseDimensions.central_diam / 2) *
            convRatio *
            (reverseConfig ? 1 : -1),
          posYLow +
            ((Math.tan(slope) * distCentreRCJConfigFootExt) / 2 -
              drawingBaseDimensions.return_channel_thickness / 2) *
              convRatio,
          (distCentreRCJConfigFootExt / 2) * convRatio,
        ]}
        rotation={[-slope, 0, 0]}
      >
        <boxGeometry
          args={[
            drawingBaseDimensions.belt_width * convRatio,
            depth,
            distCentreRCJConfigFootExt * convRatio,
          ]}
        />
        {materialsReturn.map((material, index) => (
          <primitive
            key={index}
            attach={`material-${index}`}
            object={material}
          />
        ))}
      </mesh>
    </mesh>
  );
}

export default ReturnChannel;
